// La categoría de cada producto de una grilla, para filtrar
// (ADR-2026-10-22-filtro-por-categoria-en-productos). La grilla maneja VARIANTES y la categoría es
// del PRODUCTO (`productos_categorias`), así que son dos pedidos: variante → producto y producto →
// categoría, de a 100 ids (`fetchInChunks`). Se piden recién cuando hay un filtro elegido (`activo`):
// sin filtro, la grilla no paga nada.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { fetchInChunks } from "../utils/supabaseChunkedIn";

export function useCategoriaDeCadaProducto(varianteIds: string[], activo: boolean) {
  /** variante → categoría (null = sin categoría). `null` mientras no llegó la primera vez. */
  const [porVariante, setPorVariante] = useState<Map<string, string | null> | null>(null);
  /** La última lectura falló: el filtro no puede decir nada (review Greptile del PR #198). */
  const [error, setError] = useState(false);
  const [version, setVersion] = useState(0);
  const clave = varianteIds.join(",");

  useEffect(() => {
    if (!activo || !clave) return;
    let cancelado = false;
    (async () => {
      const ids = clave.split(",");
      const variantes = await fetchInChunks<{ id: string; product_id: string | null }>(ids, (chunk) =>
        supabase.from("variantes").select("id, product_id").in("id", chunk) as unknown as PromiseLike<{
          data: Array<{ id: string; product_id: string | null }> | null;
          error: unknown;
        }>,
      );
      const productoIds = [...new Set(variantes.map((v) => v.product_id).filter((x): x is string => !!x))];
      const vinculos = await fetchInChunks<{ producto_id: string; categoria_id: string }>(productoIds, (chunk) =>
        supabase.from("productos_categorias").select("producto_id, categoria_id").in("producto_id", chunk) as unknown as PromiseLike<{
          data: Array<{ producto_id: string; categoria_id: string }> | null;
          error: unknown;
        }>,
      );
      if (cancelado) return;
      const porProducto = new Map(vinculos.map((v) => [v.producto_id, v.categoria_id]));
      setError(false);
      setPorVariante(
        new Map(variantes.map((v) => [v.id, v.product_id ? (porProducto.get(v.product_id) ?? null) : null])),
      );
    })().catch((err) => {
      console.error("[useCategoriaDeCadaProducto] no se pudieron leer las categorías", err);
      // Sin datos el filtro no puede decir nada. Un mapa vacío solo hacía que todos parecieran "Sin
      // categoría": queda el error, y quien filtra muestra todo y ofrece reintentar.
      if (!cancelado) {
        setError(true);
        setPorVariante(new Map());
      }
    });
    return () => {
      cancelado = true;
    };
  }, [activo, clave, version]);

  /** Después de guardar un producto (su categoría pudo cambiar). */
  const recargar = useCallback(() => setVersion((v) => v + 1), []);

  return { porVariante, cargando: activo && porVariante === null, error: activo && error, recargar };
}
